import clsx from "clsx";
import { MouseEvent, ReactNode } from "react";

import { Loading } from "@chef/icons/small";

import type { UIVariant, UISize } from "@chef/types/common";

type IconButtonProps = {
  variant?: UIVariant;
  size?: UISize;
  id?: string;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  label: string;
  loading?: boolean;
};

export const IconButton = ({
  className,
  id,
  variant = "default",
  size = "default",
  disabled,
  children,
  label,
  onClick,
  loading,
}: IconButtonProps) => {
  const sizeStyles = {
    xl: "w-12 h-12",
    large: "w-11 h-11",
    default: "w-10 h-10",
    small: "w-9 h-9",
    tiny: "w-8 h-8",
  }[size];

  const variantStyles = {
    default: clsx(
      "text-black bg-grey-2",
      disabled && "text-grey-1",
      !disabled && "hover:bg-grey-2/80",
    ),
    primary: clsx(
      "text-white th-lmk:text-grey-3 bg-primary",
      disabled && "bg-disabled hover:none",
      !disabled && "hover:bg-hover",
    ),
    secondary: clsx(
      "text-white bg-secondary",
      disabled && "bg-secondary/40",
      !disabled && "hover:bg-secondary/90",
    ),
    outlined: clsx(
      "border-solid border-2 focus:border-2 focus:border-primary border-primary th-gl:border-black th-rn:border-black text-black",
      disabled &&
        "focus:border-disabled border-disabled text-grey-1 th-gl:border-grey-2 th-rn:border-grey-3",
      !disabled &&
        "hover:th-amk:bg-primary/10 hover:th-lmk:bg-primary/10 hover:th-gl:border-grey-1 hover:th-amk:border-hover hover:th-lmk:border-hover",
    ),
  }[variant];

  return (
    <button
      onClick={onClick}
      type="button"
      id={id}
      disabled={disabled || loading}
      aria-label={label}
      className={clsx(
        "relative rounded-full flex items-center justify-center a11y-focus:focus-ring disabled:cursor-not-allowed",
        sizeStyles,
        variantStyles,
        className,
      )}
      aria-busy={loading}
    >
      {loading ? (
        <Loading className="absolute inset-0 m-auto animate-spin" />
      ) : (
        children
      )}
    </button>
  );
};
